import type { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { canvasToEmu, emuToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { CommentType, DeltaOf, ISlideData } from '@tencent/slide-sdk-model';
import type { ICommentGroup, ICommentGroupMap } from '@tencent/slide-sdk-model/src/data/comment-data';
import _set from 'lodash/set';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

export class SlideCommentConverter {
    /**
     * 数据3.0转换接口
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<ICommentGroupMap>,
        uiData: ISlideModel,
        shapeList: any[],
        diffCommentGroupMap?: DeepReadonly<ICommentGroupMap>,
    ): void {
        if (diffCommentGroupMap) {
            this.handleDiffCommentGroupSdkToUi(diffCommentGroupMap, uiData);
            return;
        }
        const shapeIds: string[] = [];  // 用于首次转换的场景，辅助判断commentGroup中的shapeId是否有效
        this.initShapeIds(shapeList, shapeIds);
        const commentGroupMap = new Map();
        Object.keys(sdkData).forEach((groupId) => {
            commentGroupMap.set(groupId, this.convertCommentGroupSdkToUi(sdkData[groupId], shapeIds));
        });
        uiData.commentGroupMap = commentGroupMap;
        eventBus.emit(EventKeys.SLIDE_MODEL_CONVERTED_COMMENT, { slideModel: uiData });
    }

    public static convertPartialDataFromUiToSdk(
        partialUiData: DeepReadonly<DeepPartial<ISlideModel>>,
        sdkData: DeltaOf<ISlideData>,
    ): void {
        if (partialUiData.commentGroupMap) {
            const sdkCommentGroupMap = new Map();
            partialUiData.commentGroupMap.forEach((uiCommentGroup, groupId) => {
                // @ts-ignore
                sdkCommentGroupMap.set(groupId, this.convertCommentGroupUiToSdk(uiCommentGroup));
            });
            _set(sdkData, 'commentGroupMap', sdkCommentGroupMap);
        }
    }

    public static convertCommentGroupSdkToUi(sdkCommentGroup: ICommentGroup, shapeIds?: string[]): ICommentGroup {
        if (sdkCommentGroup.commentType === CommentType.element) {
            // sdk commentGroup中的shapeId无效，表明为删除 被批注的形状 的场景，此时等价于背景批注。
            if (shapeIds && shapeIds.length > 0 && !shapeIds.includes(sdkCommentGroup.shapeId!)) {
                return {
                    commentType: CommentType.slide,
                    createdTimeStamp: sdkCommentGroup.createdTimeStamp,
                };
            }
        }
        const commentGroup: ICommentGroup = {
            commentType: sdkCommentGroup.commentType,
            createdTimeStamp: sdkCommentGroup.createdTimeStamp,
        };
        if (sdkCommentGroup.commentType !== CommentType.slide) {
            commentGroup.shapeId = sdkCommentGroup.shapeId;
        }
        if (typeof sdkCommentGroup.anchorPosition?.x !== 'undefined') {
            _set(commentGroup, 'anchorPosition.x', emuToCanvas(sdkCommentGroup.anchorPosition.x));
        }
        if (typeof sdkCommentGroup.anchorPosition?.y !== 'undefined') {
            _set(commentGroup, 'anchorPosition.y', emuToCanvas(sdkCommentGroup.anchorPosition.y));
        }
        if (sdkCommentGroup.textAnchor) {
            // len不存在，表明为删除 被批注的文字 场景，等价于形状批注。
            if (!sdkCommentGroup.textAnchor.len) {
                commentGroup.commentType = CommentType.element;
            } else {
                _set(commentGroup, 'textAnchor', sdkCommentGroup.textAnchor);
            }
        }
        return commentGroup;
    }

    public static convertCommentGroupUiToSdk(uiCommentGroup: ICommentGroup): ICommentGroup {
        const commentGroup: ICommentGroup = {
            commentType: uiCommentGroup.commentType,
            shapeId: uiCommentGroup.shapeId,
        };
        if (typeof uiCommentGroup.anchorPosition?.x !== 'undefined') {
            _set(commentGroup, 'anchorPosition.x', canvasToEmu(uiCommentGroup.anchorPosition.x));
        }
        if (typeof uiCommentGroup.anchorPosition?.y !== 'undefined') {
            _set(commentGroup, 'anchorPosition.x', canvasToEmu(uiCommentGroup.anchorPosition.y));
        }
        if (uiCommentGroup.textAnchor) {
            commentGroup.textAnchor = uiCommentGroup.textAnchor;
        }
        if (typeof uiCommentGroup.textAnchor?.cp !== 'undefined') {
            _set(commentGroup, 'textAnchor.cp', canvasToEmu(uiCommentGroup.textAnchor.cp));
        }
        return commentGroup;
    }

    private static handleDiffCommentGroupSdkToUi(
        diffCommentGroupMap: DeepReadonly<ICommentGroupMap>,
        uiData: ISlideModel,
    ) {
        Object.keys(diffCommentGroupMap).forEach((groupId) => {
            const sdkCommentGroup = diffCommentGroupMap[groupId];
            if (!uiData.commentGroupMap) {
                uiData.commentGroupMap = new Map();
            }
            if (!sdkCommentGroup) {
                uiData.commentGroupMap.delete(groupId);
            } else {
                uiData.commentGroupMap.set(groupId, this.convertCommentGroupSdkToUi(sdkCommentGroup));
            }
        });
    }

    private static initShapeIds(shapeList: any[], shapeIds: string[]) {
        shapeList.forEach((shape) => {
            if (shape.shapeList) {
                shapeIds.push(shape.id);
                this.initShapeIds(shape.shapeList, shapeIds);
            } else {
                shapeIds.push(shape.id);
            }
        });
    }
}
